/*
  Custom Header JavaScript for Dawn Theme
  Place this in assets/custom-header.js
*/

class CustomHeader {
  constructor() {
    this.header = document.querySelector('.header-wrapper');
    this.searchInput = document.getElementById('header-search-input');
    this.cartIcon = document.getElementById('cart-icon-bubble');
    this.consultationBtn = document.querySelector('.consultation-btn');
    
    this.init();
  }

  init() {
    this.handleStickyHeader();
    this.initializeSearch();
    this.initializeCartUpdate();
    this.handleConsultationClick();
    this.initializeMobileMenu();
  }

  // Sticky Header with Scroll Effect
  handleStickyHeader() {
    let lastScroll = 0;
    
    window.addEventListener('scroll', () => {
      const currentScroll = window.pageYOffset;
      
      if (currentScroll > 50) {
        this.header.classList.add('scrolled');
      } else {
        this.header.classList.remove('scrolled');
      }
      
      lastScroll = currentScroll;
    });
  }

  // Advanced Search Functionality
  initializeSearch() {
    if (!this.searchInput) return;

    let searchTimeout;
    let suggestionsContainer = this.createSuggestionsContainer();

    this.searchInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();
      
      clearTimeout(searchTimeout);
      
      if (query.length < 2) {
        this.hideSuggestions(suggestionsContainer);
        return;
      }

      searchTimeout = setTimeout(() => {
        this.performSearch(query, suggestionsContainer);
      }, 300);
    });

    // Handle Enter key
    this.searchInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        const query = e.target.value.trim();
        if (query) {
          window.location.href = `/search?q=${encodeURIComponent(query)}`;
        }
      }
    });

    // Close suggestions on outside click
    document.addEventListener('click', (e) => {
      if (!this.searchInput.contains(e.target) && !suggestionsContainer.contains(e.target)) {
        this.hideSuggestions(suggestionsContainer);
      }
    });
  }

  createSuggestionsContainer() {
    const container = document.createElement('div');
    container.className = 'search-suggestions';
    this.searchInput.parentElement.appendChild(container);
    return container;
  }

  async performSearch(query, container) {
    try {
      // Shopify predictive search API
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`
      );
      
      const data = await response.json();
      this.displaySuggestions(data.resources.results.products || [], container);
    } catch (error) {
      console.error('Search error:', error);
    }
  }

  displaySuggestions(products, container) {
    if (products.length === 0) {
      container.innerHTML = '<div style="padding: 1rem; text-align: center; color: #999;">No results found</div>';
      container.classList.add('active');
      return;
    }

    const html = products.map(product => `
      <a href="${product.url}" class="search-suggestion-item">
        ${product.image ? `<img src="${product.image}" alt="${product.title}" class="suggestion-image">` : ''}
        <div class="suggestion-details">
          <div class="suggestion-title">${product.title}</div>
          <div class="suggestion-price">${this.formatMoney(product.price)}</div>
        </div>
      </a>
    `).join('');

    container.innerHTML = html;
    container.classList.add('active');
  }

  hideSuggestions(container) {
    container.classList.remove('active');
  }

  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  // Cart Update Functionality
  initializeCartUpdate() {
    if (!this.cartIcon) return;

    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      this.updateCartCount(event.detail.cart);
    });

    // Fetch initial cart count
    this.fetchCartCount();
  }

  async fetchCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateCartCount(cart);
    } catch (error) {
      console.error('Cart fetch error:', error);
    }
  }

  updateCartCount(cart) {
    const existingBubble = this.cartIcon.querySelector('.cart-count-bubble');
    
    if (cart.item_count > 0) {
      if (existingBubble) {
        existingBubble.textContent = cart.item_count;
      } else {
        const bubble = document.createElement('span');
        bubble.className = 'cart-count-bubble';
        bubble.textContent = cart.item_count;
        this.cartIcon.appendChild(bubble);
      }
    } else if (existingBubble) {
      existingBubble.remove();
    }
  }

  // Consultation Button Handler
  handleConsultationClick() {
    if (!this.consultationBtn) return;

    this.consultationBtn.addEventListener('click', (e) => {
      // Track consultation click (add your analytics here)
      if (window.gtag) {
        gtag('event', 'click', {
          'event_category': 'Consultation',
          'event_label': 'Header Consultation Button'
        });
      }

      // Allow default navigation to proceed
    });
  }

  // Mobile Menu Initialization
  initializeMobileMenu() {
    const mobileToggle = document.querySelector('.mobile-menu-toggle');
    if (!mobileToggle) return;

    mobileToggle.addEventListener('click', () => {
      document.body.classList.toggle('mobile-menu-open');
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CustomHeader();
  });
} else {
  new CustomHeader();
}

// Helper function to add items to cart (if needed)
async function addToCart(variantId, quantity = 1) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });

    const data = await response.json();
    
    // Trigger cart update event
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart: await fetch('/cart.js').then(r => r.json()) }
    }));

    return data;
  } catch (error) {
    console.error('Add to cart error:', error);
    throw error;
  }
}

// Export for use in other scripts
window.CustomHeader = CustomHeader;
window.addToCart = addToCart;